"use client";

import type { ReactNode } from "react";

import React, { useCallback, useState } from "react";
import dynamic from "next/dynamic";
import { useModal } from "@hooks/useModal";
import ServiceTemplatePage from "@components/services/ServiceTemplatePage";
import SectionHead from "@components/services/SectionHead";
import AnswerCard from "@components/services/AnswerCard";
import KeyFactsBar from "@components/services/KeyFactsBar";
import ScopeGrid from "@components/services/ScopeGrid";
import PricingTiers from "@components/services/PricingTiers";
import WhyDbiCards from "@components/services/WhyDbiCards";
import ProcessSteps from "@components/services/ProcessSteps";
import ComparisonTable from "@components/services/ComparisonTable";
import TrustSignalsBand from "@components/services/TrustSignalsBand";
import SocialProofQuote from "@components/services/SocialProofQuote";
import ManagedTeaserBlock from "@components/services/ManagedTeaserBlock";
import BeforeAfterSlider from "@components/ui/BeforeAfterSlider";
import StickyPriceBar from "@components/sections/StickyPriceBar";
import PricingCalculator from "@components/features/pricing-calculator/PricingCalculator";
import type { PricingCalculatorData } from "@components/features/pricing-calculator/types";
import type { StickyPriceBarData } from "@components/sections/StickyPriceBar";

const Modal = dynamic(() => import("@components/modals/Modal"), {
  ssr: false,
});

const DemoForm = dynamic(
  () => import("@components/features/demo-form/DemoForm"),
  { ssr: false },
);

export interface PageData {
  hero: {
    title: string;
    highlightedText: string;
    description: string;
    backgroundImage: string;
    trustItems: string[];
    beforeAfter: {
      beforeLabel: string;
      afterLabel: string;
      beforeImage: string;
      afterImage: string;
      beforeAlt: string;
      afterAlt: string;
    };
  };
  answer: {
    question: string;
    text?: string;
    paragraphs?: string[];
    keyfacts?: { label: string; sublabel: string }[];
  };
  problem: {
    title: string;
    items: {
      title: string;
      description: string;
    }[];
  };
  calculator: PricingCalculatorData & { title: string };
  packages: {
    seoChip: string;
    title: string;
    subtitle?: string;
    layout: "grid" | "rows";
    items: {
      name: string;
      price: number;
      tagline: string;
      features: string[];
      cta?: { label: string; href: string };
    }[];
  };
  faq: {
    faqs: { question: string; answer: string }[];
  };
  serviceSubnav: { id: string; label: string; section: string }[];
  relatedServices: {
    tag: string;
    title: string;
    description: string;
    href: string;
  }[];
  ctaBlock: {
    title: string;
    description: string;
    primaryCta: { label: string; href: string };
    secondaryCta?: { label: string; href: string };
  };
  whyDbi?: {
    title: string;
    intro: string;
    image?: string;
    imageAlt?: string;
    cards: {
      id: string;
      icon?: string;
      title: string;
      description: string;
      note?: string;
      subheading?: string;
      subtext?: string;
    }[];
  };
  process?: {
    title: string;
    description?: string;
    /** Short delivery-time pill, e.g. "24 Stunden". */
    deliveryBadge?: string;
    steps: { number: number; title: string; description: string }[];
  };
  comparison?: {
    title: string;
    intro?: string;
    highlightColumn?: "left" | "right" | "powerbi" | "excel";
    columnLabels?: { criterion?: string; left?: string; right?: string };
    rows: { criterion: string; excel: string; powerbi: string }[];
  };
  trustSignals?: {
    items: { icon?: string; label: string; sublabel?: string }[];
  };
  socialProof?: {
    subtitle?: string;
    trustNote?: string;
    items: {
      id: string;
      industry?: string;
      companySize?: string;
      quote: string;
      attribution?: string;
      context?: string;
      metrics?: { value: string; label: string }[];
      tags?: string[];
      logo?: string;
      logoAlt?: string;
    }[];
  };
  priceExamples?: {
    title?: string;
    rows: { config: string; price: string; package: string }[];
    footnote?: string;
  };
  managedTeaser?: {
    eyebrow?: string;
    title: string;
    titleHighlight?: string;
    description: string;
    bullets: { icon?: string; title: string; description: string }[];
    primaryCta: { text: string; href: string };
    secondaryCta?: { text: string; href: string };
    footnote?: string;
  };
  stickyBar: StickyPriceBarData;
}

interface DashboardLandingClientProps {
  data: PageData;
  /** GuideReadNext for this route, rendered by the server page. */
  guideSlot: ReactNode;
  /** ServiceContactCard for this route, rendered by the server page. */
  contactSlot: ReactNode;
}

export default function DashboardLandingClient({ data, guideSlot, contactSlot }: DashboardLandingClientProps) {
  const demoModal = useModal();
  const openDemoModal = demoModal.open;
  /* Carries the calculator configuration into the lead form's message field. */
  const [quoteSummary, setQuoteSummary] = useState("");

  const handleRequestQuote = useCallback(
    (summary: string) => {
      setQuoteSummary(summary);
      openDemoModal();
    },
    [openDemoModal],
  );

  if (!data.serviceSubnav) {
    throw new Error("DashboardLandingClient: missing `serviceSubnav` in service JSON.");
  }
  if (!data.relatedServices) {
    throw new Error("DashboardLandingClient: missing `relatedServices` in service JSON.");
  }
  if (!data.ctaBlock) {
    throw new Error("DashboardLandingClient: missing `ctaBlock` in service JSON.");
  }
  const calculatorSubnav = data.serviceSubnav.find((item) => item.section === "calculator");
  if (!calculatorSubnav) {
    throw new Error(
      "DashboardLandingClient: missing calculator section in `serviceSubnav`.",
    );
  }

  const hero = {
    badge: "Festpreis Service",
    title: data.hero.title,
    highlight: data.hero.highlightedText,
    highlightPosition: "after" as const,
    description: data.hero.description,
    leadMeta: data.hero.trustItems,
    photoSrc: data.hero.backgroundImage,
    photoAlt: data.hero.title,
    tag: "Microsoft Solutions Partner",
    primaryCta: {
      label: "Jetzt Kalkulieren",
      href: "#calculator",
      variant: "primary" as const,
    },
    secondaryCta: {
      label: "Leistungsumfang ansehen",
      href: "#leistungen",
      variant: "ghost-inverse" as const,
    },
  };

  /* A section spec knows its own TOC label and eyebrow topic; the running number
     is handed to `render` from the section's position in the final list. One
     ordered list therefore drives the section order, the `Inhalt` TOC and the
     eyebrow numbering at once — a section can no longer be rendered without a
     TOC entry (only 5 of 12 used to appear), and the numbers can no longer drift
     out of sequence (05 appeared twice, 10 was skipped). */
  interface SectionSpec {
    id: string;
    label: string;
    topic: string;
    /** false for sections that carry their own heading and show no eyebrow —
     *  they must not consume a number, or the visible sequence gains a gap. */
    numbered?: boolean;
    render: (eyebrow: string) => React.ReactNode;
  }

  const renderByKey: Record<
    string,
    { topic: string; numbered?: boolean; render: (eyebrow: string) => React.ReactNode }
  > = {
    answer: {
      topic: "Direkte Antwort",
      render: (eyebrow) => (
        <>
          <SectionHead eyebrow={eyebrow} title={data.answer.question} blue />
          <AnswerCard
            paragraphs={
              data.answer.paragraphs ??
              (data.answer.text ? [data.answer.text] : [])
            }
          />
          <KeyFactsBar facts={data.answer.keyfacts} />
        </>
      ),
    },
    scope: {
      topic: "Migrationsergebnis",
      render: (eyebrow) => (
        <>
          <SectionHead eyebrow={eyebrow} title={data.problem.title} blue />
          <ScopeGrid items={data.problem.items} />
        </>
      ),
    },
    beforeAfter: {
      topic: "Vorher / Nachher",
      render: (eyebrow) => (
        <>
          <SectionHead
            eyebrow={eyebrow}
            title="Von Excel oder Tableau zum Power-BI-Dashboard"
            blue
          />
          <BeforeAfterSlider
            beforeImage={data.hero.beforeAfter.beforeImage}
            afterImage={data.hero.beforeAfter.afterImage}
            beforeLabel={data.hero.beforeAfter.beforeLabel}
            afterLabel={data.hero.beforeAfter.afterLabel}
            beforeAlt={data.hero.beforeAfter.beforeAlt}
            afterAlt={data.hero.beforeAfter.afterAlt}
          />
        </>
      ),
    },
    calculator: {
      /* The calculator ships its own heading, so it takes no eyebrow. */
      topic: "Preisrechner",
      numbered: false,
      render: () => (
        <>
          <StickyPriceBar data={data.stickyBar} />
          <PricingCalculator
            data={data.calculator}
            onRequestQuote={handleRequestQuote}
          />
        </>
      ),
    },
    pricing: {
      topic: "Pakete",
      render: (eyebrow) => (
        <>
          <SectionHead eyebrow={eyebrow} title={data.packages.title} blue />
          <PricingTiers
            layout={data.packages.layout}
            tiers={data.packages.items.map((item, index) => ({
              name: item.name,
              price: String(item.price),
              sub: item.tagline,
              features: item.features,
              featured: index === 1,
              cta: item.cta,
            }))}
          />
        </>
      ),
    },
  };

  const specs: SectionSpec[] = data.serviceSubnav
    .filter((item) => item.section !== "faq")
    .map((item) => {
      const entry = renderByKey[item.section];
      if (!entry) {
        throw new Error(
          `DashboardLandingClient: unsupported section key '${item.section}' in serviceSubnav.`
        );
      }
      return {
        id: item.id,
        label: item.label,
        topic: entry.topic,
        numbered: entry.numbered,
        render: entry.render,
      };
    });

  if (data.whyDbi) {
    const whyDbi = data.whyDbi;
    specs.push({
      id: "why-dbi",
      label: "Warum DBI",
      topic: "Warum DBI",
      render: (eyebrow) => (
        <>
          <SectionHead eyebrow={eyebrow} title={whyDbi.title} blue />
          <WhyDbiCards
            cards={whyDbi.cards}
            intro={whyDbi.intro}
            image={whyDbi.image}
            imageAlt={whyDbi.imageAlt}
          />
        </>
      ),
    });
  }

  if (data.process) {
    const process = data.process;
    specs.push({
      id: "prozess-details",
      label: "Prozess",
      topic: "Migrationsprozess",
      render: (eyebrow) => (
        <>
          <SectionHead
            eyebrow={eyebrow}
            title={process.title}
            lede={process.description}
            blue
          />
          <ProcessSteps steps={process.steps} badge={process.deliveryBadge} />
        </>
      ),
    });
  }

  if (data.comparison) {
    const comparison = data.comparison;
    specs.push({
      id: "vergleich",
      label: "Vergleich",
      topic: "Vergleich",
      render: (eyebrow) => (
        <>
          <SectionHead eyebrow={eyebrow} title={comparison.title} blue />
          <ComparisonTable
            rows={comparison.rows}
            intro={comparison.intro}
            columnLabels={comparison.columnLabels}
            highlightColumn={comparison.highlightColumn}
          />
        </>
      ),
    });
  }

  if (data.trustSignals) {
    const trustSignals = data.trustSignals;
    specs.push({
      id: "trust-signals",
      label: "Vertrauen",
      topic: "Trust Signals",
      render: (eyebrow) => (
        <>
          <SectionHead eyebrow={eyebrow} title="Warum Teams mit DBI arbeiten" blue />
          <TrustSignalsBand items={trustSignals.items} />
        </>
      ),
    });
  }

  if (data.socialProof) {
    const socialProof = data.socialProof;
    specs.push({
      id: "social-proof",
      label: "Referenzen",
      topic: "Aus der Praxis",
      render: (eyebrow) => (
        <>
          <SectionHead eyebrow={eyebrow} title="Referenzprojekte & Kundenstimmen" blue />
          <SocialProofQuote
            items={socialProof.items}
            subtitle={socialProof.subtitle}
            trustNote={socialProof.trustNote}
          />
        </>
      ),
    });
  }

  if (data.managedTeaser) {
    const managedTeaser = data.managedTeaser;
    specs.push({
      id: "managed-teaser",
      label: "Day-2 Betrieb",
      topic: "Day-2 Operations",
      render: (eyebrow) => (
        <>
          <SectionHead
            eyebrow={eyebrow}
            title="Nach dem Go-Live: Betrieb weiter automatisieren"
            blue
          />
          <ManagedTeaserBlock
            eyebrow={managedTeaser.eyebrow}
            title={managedTeaser.title}
            titleHighlight={managedTeaser.titleHighlight}
            description={managedTeaser.description}
            bullets={managedTeaser.bullets}
            primaryCta={managedTeaser.primaryCta}
            secondaryCta={managedTeaser.secondaryCta}
            footnote={managedTeaser.footnote}
          />
        </>
      ),
    });
  }

  let sectionNumber = 0;
  const sections = specs.map((spec) => {
    const eyebrow =
      spec.numbered === false
        ? ""
        : `${String(++sectionNumber).padStart(2, "0")} · ${spec.topic}`;
    return { id: spec.id, content: spec.render(eyebrow) };
  });

  const faqNumber = String(sectionNumber + 1).padStart(2, "0");
  const faqSubnav = data.serviceSubnav.find((item) => item.section === "faq");

  return (
    <>
    <ServiceTemplatePage
      hero={hero}
      subnav={[
        ...specs.map((spec) => ({ id: spec.id, label: spec.label })),
        ...(faqSubnav ? [{ id: faqSubnav.id, label: faqSubnav.label }] : []),
      ]}
      sections={sections}
      faqEyebrow={`${faqNumber} · Häufige Fragen`}
      faqTitle="Fragen vor dem Projektstart"
      faqs={data.faq.faqs}
      relatedServices={data.relatedServices}
      guideSlot={guideSlot}
      contactSlot={contactSlot}
      ctaBlock={{
        title: data.ctaBlock.title,
        description: data.ctaBlock.description,
        primaryCta: {
          label: data.ctaBlock.primaryCta.label,
          href: data.ctaBlock.primaryCta.href,
        },
        secondaryCta: data.ctaBlock.secondaryCta
          ? {
              label: data.ctaBlock.secondaryCta.label,
              href: data.ctaBlock.secondaryCta.href,
            }
          : undefined,
      }}
    />
    <Modal
      isOpen={demoModal.isOpen}
      onClose={demoModal.close}
      title="Anfrage stellen"
      variant="form"
      size="lg"
      className="max-w-4xl"
      fullscreenOnMobile={true}
      showCloseButton={true}
      closeOnOverlayClick={true}
      closeOnEscape={true}
    >
      <DemoForm onClose={demoModal.close} initialMessage={quoteSummary} />
    </Modal>
    </>
  );
}
